import React from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiTrendingUp, FiCheckCircle, FiArrowRight, FiImage, FiVideo, FiMessageCircle, FiHeart } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const MetaAdsPage = () => {
  useDocumentTitle(
    'Meta Ads Services in India | Trusted by India\'s Best Advertising Agencies',
    'Get clear, result-driven campaigns from a performance marketing company that understands real business goals. A dedicated Meta Ads expert manages your targeting, creatives, and scaling with simplicity.'
  );
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="flex justify-center items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl text-center"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Meta Ads Management for Growth‑Focused Brands
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl text-white/90 mb-6 leading-relaxed">
                Reach more customers on Facebook and Instagram with a performance marketing company that treats every rupee like it's its own. Mirani Marketing helps you plan, launch, and scale Meta Ads that drive real leads and sales, not just clicks.
              </p>
              <p className="text-base md:text-lg text-white/80 mb-8 italic">
                "Trusted by brands that search for top advertising agencies in India but choose a focused, performance-first partner instead."
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Get a Free Meta Ads Audit</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
                >
                  <span>Talk to Our Performance Team</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Meta Ads */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is Meta Ads Management?</h2>
            <h3 className="text-2xl font-semibold text-blue-600 mb-6">Run ads that turn scrolls into sales</h3>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              Meta Ads management means handling everything for your Facebook and Instagram campaigns—strategy, creatives, targeting, tracking, and daily optimisation—so your brand can grow faster while you focus on running the business.
            </p>
            <div className="text-left max-w-3xl mx-auto space-y-4 text-gray-600">
              <p>✓ Clear strategy based on your goals (leads, sales, bookings, or app installs).</p>
              <p>✓ Smart use of Meta's AI and Advantage+ features to find the right people at the right time.</p>
              <p>✓ Transparent reporting so you always know what you're paying for and what you're getting back.</p>
              <p className="text-sm italic text-gray-500 mt-6">"As a performance marketing company, Mirani Marketing builds Meta campaigns that can stand next to India's best advertising agencies while staying lean, agile, and accountable."</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiImage className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Feed Ads</h3>
              <p className="text-gray-600 leading-relaxed">
                Eye-catching image and video ads that appear seamlessly in Facebook and Instagram feeds, perfect for brand awareness and engagement.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiVideo className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Stories & Reels</h3>
              <p className="text-gray-600 leading-relaxed">
                Full-screen vertical ads on Stories and Reels that capture attention with immersive content and drive high engagement rates.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiMessageCircle className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Lead Generation</h3>
              <p className="text-gray-600 leading-relaxed">
                Collect leads directly on Facebook and Instagram with pre-filled forms that make it easy for users to express interest in your business.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiHeart className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Engagement Campaigns</h3>
              <p className="text-gray-600 leading-relaxed">
                Drive likes, comments, shares, and page follows to build a strong social media community around your brand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Meta Ads Process</h2>
            <p className="text-lg text-gray-500 italic max-w-4xl mx-auto mb-2">
              This approach keeps your Meta Ads competitive with campaigns run by top advertising agencies in India, but with more direct access to the team handling your account.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                step: '01',
                title: 'Deep Discovery & Market Research',
                description: 'Understand your offer, margins, target audience, and competitors so we know exactly who to show your ads to and what message will convert.',
                icon: FiUsers
              },
              {
                step: '02',
                title: 'Offer, Funnel & Creative Planning',
                description: 'Map out the full journey—from ad to landing page to lead or purchase—and plan creatives that feel native to Facebook and Instagram, not like generic ads.',
                icon: FiImage
              },
              {
                step: '03',
                title: 'Campaign Setup & Tracking',
                description: 'Create campaigns, ad sets, and ads with proper pixels, events, and conversions API so Meta can optimise using real, clean data.',
                icon: FiTrendingUp
              },
              {
                step: '04',
                title: 'Daily Optimisation & Testing',
                description: 'Test audiences, creatives, placements, and bids. Pause what is wasting money and push what is working to get a better return on ad spend.',
                icon: FiCheckCircle
              },
              {
                step: '05',
                title: 'Reporting, Learnings & Scaling',
                description: 'Share easy-to-read reports with clear next steps so you know when and how much to scale.',
                icon: FiVideo
              }
            ].map((process, index) => (
              <div key={index} className="flex gap-6 items-start group">
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-2xl font-bold w-16 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                    {process.step}
                  </div>
                </div>
                <div className="flex-grow bg-gray-50 rounded-xl p-6 group-hover:shadow-xl transition-all duration-300">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Meta Ads Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible pricing for businesses of all sizes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Starter',
                price: '₹18,000',
                duration: '/month',
                popular: false,
                features: [
                  'Ad Spend: Up to ₹40,000',
                  'Facebook + Instagram',
                  'Feed & Stories Ads',
                  'Basic Targeting',
                  'Monthly Reports',
                  'Email Support'
                ]
              },
              {
                name: 'Growth',
                price: '₹35,000',
                duration: '/month',
                popular: true,
                features: [
                  'Ad Spend: Up to ₹1,20,000',
                  'All Placements',
                  'Reels Ads',
                  'Advanced Targeting',
                  'Lead Gen Campaigns',
                  'A/B Testing',
                  'Bi-Weekly Reports',
                  'Priority Support'
                ]
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                duration: 'pricing',
                popular: false,
                features: [
                  'Unlimited Ad Spend',
                  'Multi-Platform Strategy',
                  'Video Production',
                  'Influencer Campaigns',
                  'Custom Audiences',
                  'Weekly Reports',
                  'Dedicated Manager',
                  '24/7 Support'
                ]
              }
            ].map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-blue-500 shadow-2xl' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{pkg.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">{pkg.duration}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <FiCheckCircle className="text-blue-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Mirani Marketing for Meta Ads?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Performance‑driven approach</h3>
              <p className="text-gray-600 leading-relaxed">
                Mirani Marketing is a performance marketing company first, so every decision—from targeting to creatives—is tied to revenue and ROI, not just vanity metrics.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiUsers className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Experience with Indian and global brands</h3>
              <p className="text-gray-600 leading-relaxed">
                The team understands what works in India and in markets like the US, UK, Canada, and Australia, which is why many clients see Mirani as their alternative to India's best advertising agencies without the big‑agency layers.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiCheckCircle className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Clear communication & transparent reporting</h3>
              <p className="text-gray-600 leading-relaxed">
                You always know where your budget goes, which campaigns are working, and what will be tested next.
              </p>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 italic mt-8 max-w-3xl mx-auto">
            "Whether you are searching for a top advertising agency in India or a specialised Meta Ads partner, Mirani Marketing gives you the focus and performance you need to grow."
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about Meta Ads services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "What results can I expect from Meta Ads?",
                answer: "You can expect more qualified traffic, leads, and sales as campaigns are optimised. Exact numbers depend on your industry, offer, and current brand strength, but the goal is always to improve your cost per lead or sale over time."
              },
              {
                question: "How much budget do I need to start?",
                answer: "Meta Ads can work with smaller budgets, but having room to test creatives and audiences is important. Many clients start with a test budget and then scale once winning campaigns are found."
              },
              {
                question: "How is this different from working with big advertising agencies?",
                answer: "Large agencies can be slow and layered. Mirani Marketing offers direct access to the people running your ads, faster changes, and a stronger focus on measurable performance."
              },
              {
                question: "Do you only run Meta Ads?",
                answer: "No. Meta Ads are one part of a larger growth system that can also include Google Ads, SEO, and other performance channels, depending on your goals."
              },
              {
                question: "How soon can I see results?",
                answer: "Most accounts see clear data and early signals in 2–4 weeks. Stable, scalable results usually come after a few rounds of testing and optimisation."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-blue-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-blue-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Scale with Meta Ads?</h2>
          <p className="text-2xl mb-12 text-white/90 max-w-3xl mx-auto">
            Work with a performance marketing company that understands both numbers and creativity. If you are comparing top advertising agencies in India, add Mirani Marketing to your shortlist and see how focused Meta Ads can change your growth.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              <span>Book a Free Meta Ads Strategy Call</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              <span>Get Your Meta Ads Audit</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MetaAdsPage;
